import { useEffect, useState } from "react";
import { api, payload } from "./lib/api";

// Email automatiche (Resend) per tutte le case del gruppo: ospite nella sua lingua, gestore in italiano.
type Sender = { siteId: string; name: string; fromAddress: string; fromName: string };
type EmailSettings = {
  enabled: boolean; apiKeyConfigured: boolean; ownerEmail: string; sendGuest: boolean; notifyOwner: boolean;
  senders: Sender[]; lastTestAt: string | null; lastSentAt: string | null; lastError: string | null;
};
type TestResult = { name: string; sent: boolean; error?: string };

function when(value: string | null) {
  return value ? new Date(value).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "mai";
}

export default function RentalEmail({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [draft, setDraft] = useState<EmailSettings | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ email: EmailSettings }>(`/api/sites/${siteId}/rental-email`).then((result) => { setSettings(result.email); setDraft(result.email); }).catch(() => setSettings(null));
  }, [siteId]);

  async function save(enabled: boolean) {
    if (!draft) return;
    setBusy(true); setMessage(""); setError("");
    try {
      const result = await api<{ email: EmailSettings }>(`/api/sites/${siteId}/rental-email`, payload("PUT", {
        enabled, ownerEmail: draft.ownerEmail.trim(), sendGuest: draft.sendGuest, notifyOwner: draft.notifyOwner,
        senders: draft.senders.map((sender) => ({ siteId: sender.siteId, fromAddress: sender.fromAddress.trim(), fromName: sender.fromName.trim() })),
        apiKey: apiKey.replace(/\s+/g, ""),
      }));
      setSettings(result.email); setDraft(result.email); setApiKey("");
      setMessage(enabled ? "Email automatiche attive." : "Impostazioni salvate: le email automatiche sono spente.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally { setBusy(false); }
  }

  async function sendTest() {
    setBusy(true); setMessage(""); setError(""); setTestResults([]);
    try {
      const result = await api<{ results: TestResult[] }>(`/api/sites/${siteId}/rental-email/test`, payload("POST", { email: testEmail.trim() }));
      setTestResults(result.results);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Email di prova non inviata");
    } finally { setBusy(false); }
  }

  if (!settings || !draft) return null;
  const updateSender = (siteKey: string, field: "fromAddress" | "fromName", value: string) =>
    setDraft({ ...draft, senders: draft.senders.map((sender) => sender.siteId === siteKey ? { ...sender, [field]: value } : sender) });

  return <section className="rental-payments rental-email">
    <h3>Email automatiche (Resend)</h3>
    <p className="escape-resource-note">All'ospite, nella lingua in cui ha prenotato: richiesta ricevuta, prenotazione confermata o pagata, annullamento, rimborso, pagamento non concluso. Al gestore: nuova richiesta, nuova prenotazione pagata, rimborsi.</p>
    <p className="rental-payments-state">
      {settings.enabled ? <strong>Attive</strong> : <strong>Spente</strong>}
      {" · "}chiave {settings.apiKeyConfigured ? "salvata" : "non inserita"}
      {" · "}ultima email inviata: {when(settings.lastSentAt)}
    </p>
    {settings.lastError && <p className="error-message">Ultimo errore: {settings.lastError}</p>}
    {canEdit && <>
      <label>Chiave API di Resend (inizia con re_)
        <input type="text" name="resend-api-key" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} data-1p-ignore data-lpignore="true" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder={settings.apiKeyConfigured ? "Lascia vuoto per tenere quella salvata" : "re_…"} />
      </label>
      <p className="escape-resource-note">Si crea su resend.com → API Keys (permesso «Sending access») e si incolla solo qui: mai in chat, email o file.</p>
      <label>Email del gestore (riceve le notifiche e le risposte degli ospiti)
        <input type="email" value={draft.ownerEmail} onChange={(event) => setDraft({ ...draft, ownerEmail: event.target.value })} placeholder="nome@esempio.it" />
      </label>
      {draft.senders.map((sender) => <div key={sender.siteId} className="rental-email-sender">
        <label>Mittente per {sender.name} (dominio verificato in Resend)
          <input type="email" value={sender.fromAddress} onChange={(event) => updateSender(sender.siteId, "fromAddress", event.target.value)} placeholder="prenotazioni@dominio.it" />
        </label>
        <label>Nome mittente (facoltativo)
          <input value={sender.fromName} onChange={(event) => updateSender(sender.siteId, "fromName", event.target.value)} placeholder={sender.name} />
        </label>
      </div>)}
      <label className="rental-email-check"><input type="checkbox" checked={draft.sendGuest} onChange={(event) => setDraft({ ...draft, sendGuest: event.target.checked })} /> Manda le email agli ospiti</label>
      <label className="rental-email-check"><input type="checkbox" checked={draft.notifyOwner} onChange={(event) => setDraft({ ...draft, notifyOwner: event.target.checked })} /> Avvisa il gestore di nuove richieste, pagamenti e rimborsi</label>
      <div className="rental-payments-actions">
        <button type="button" className="primary" disabled={busy} onClick={() => void save(settings.enabled)}>{busy ? "Salvataggio…" : "Salva"}</button>
        <button type="button" disabled={busy} onClick={() => void save(!settings.enabled)}>{settings.enabled ? "Spegni le email" : "Salva e accendi le email"}</button>
      </div>
      {settings.apiKeyConfigured && <div className="rental-email-test">
        <label>Manda un'email di prova (una per casa) a
          <input type="email" value={testEmail} onChange={(event) => setTestEmail(event.target.value)} placeholder="tuo indirizzo" />
        </label>
        <button type="button" disabled={busy || !testEmail.trim()} onClick={() => void sendTest()}>Invia prova</button>
        {testResults.map((result) => <p key={result.name} className={result.sent ? "success-message" : "error-message"}>{result.name}: {result.sent ? "inviata" : `non inviata (${result.error})`}</p>)}
      </div>}
    </>}
    {message && <p className="success-message">{message}</p>}
    {error && <p className="error-message">{error}</p>}
  </section>;
}
